import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { useLocale } from '@/hooks/useLocale'

type Props = {
  targetId: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  playerId: string
  name: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  voiceChatDetail: string
  playStyle: string
  fontColor: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  valorantFavoriteWeapon: string
  rank: string
  hitokoto: string
  characters: number[]
  age: number
}

type MarvelRivalsRank =
  | 'iron'
  | 'bronze'
  | 'silver'
  | 'gold'
  | 'platinum'
  | 'diamond'
  | 'immortal'
  | 'radiant'
  | 'ascendant'

export const MarvelRivals1: React.FC<Props> = ({
  playerId,
  name,
  sex,
  platform,
  fontColor,
  templateColor,
  voiceChat,
  voiceChatDetail,
  playStyle,
  playTimeWeekDayBegin,
  playTimeWeekDayEnd,
  playTimeWeekEndBegin,
  playTimeWeekEndEnd,
  valorantFavoriteWeapon,
  rank,
  hitokoto,
  characters,
  targetId,
  age,
}) => {
  const { t } = useLocale()
  useEffect(() => {
    fitty('#name', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#age', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#valorantId', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#rank', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#voiceChat', {
      maxSize: 12,
      minSize: 12,
    })
    fitty('#playStyle', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#favoriteWeapon', {
      maxSize: 20,
      minSize: 8,
    })
    fitty('#hitokoto', {
      maxSize: 20,
      minSize: 14,
    })
  }, [])

  const rankMapLabel: { [key in MarvelRivalsRank]: string } = {
    iron: t.アイアン,
    bronze: t.ブロンズ,
    silver: t.シルバー,
    gold: t.ゴールド,
    platinum: t.プラチナ,
    diamond: t.ダイヤ,
    ascendant: t.アセンダント,
    immortal: t.イモータル,
    radiant: t.レディアント,
  }

  return (
    <ProfileContainer>
      <ProfileInner id={targetId} fontColor={fontColor}>
        <Image
          width="2000"
          height="1500"
          priority={true}
          unoptimized={true}
          src={`/static/images/profiles/marvelRivals1/${templateColor}.jpeg`}
          alt={`マーベルライバルズ自己紹介カード ${templateColor}`}
        />
        <Name>
          <span id="name">{name}</span>
        </Name>
        {sex === 0 && <Sex0 />}
        {sex === 1 && <Sex1 />}
        {sex === 2 && <Sex2 />}
        {platform.includes(0) && <Platform0 />}
        {platform.includes(1) && <Platform1 />}
        {platform.includes(2) && <Platform2 />}
        {platform.includes(4) && <Platform3 />}
        <VoiceChatText>
          <span id="voiceChat">
            {voiceChat.includes(0) && ' Skype '}
            {voiceChat.includes(1) && ' LINE '}
            {voiceChat.includes(2) && ' PSVC '}
            {voiceChat.includes(3) && ' Discord '}
          </span>
        </VoiceChatText>
        {(voiceChat.includes(0) ||
          voiceChat.includes(1) ||
          voiceChat.includes(2) ||
          voiceChat.includes(3)) && <VoiceChatOk />}
        {voiceChat.includes(4) && <VoiceChatNg />}
        <PlayStyle>
          <span id="playStyle">{playStyle}</span>
        </PlayStyle>
        {characters.includes(0) && <Character0 />}
        {characters.includes(1) && <Character1 />}
        {characters.includes(2) && <Character2 />}
        {characters.includes(3) && <Character3 />}
        {characters.includes(4) && <Character4 />}
        {characters.includes(5) && <Character5 />}
        {characters.includes(6) && <Character6 />}
        {characters.includes(7) && <Character7 />}
        {characters.includes(8) && <Character8 />}
        {characters.includes(9) && <Character9 />}
        {characters.includes(10) && <Character10 />}
        {characters.includes(11) && <Character11 />}
        {characters.includes(12) && <Character12 />}
        {characters.includes(13) && <Character13 />}
        {characters.includes(14) && <Character14 />}
        {characters.includes(15) && <Character15 />}
        {characters.includes(16) && <Character16 />}
        {characters.includes(17) && <Character17 />}
        {characters.includes(18) && <Character18 />}
        {characters.includes(19) && <Character19 />}
        {characters.includes(20) && <Character20 />}
        {characters.includes(21) && <Character21 />}
        {characters.includes(22) && <Character22 />}
        {characters.includes(23) && <Character23 />}
        {characters.includes(24) && <Character24 />}
        {characters.includes(25) && <Character25 />}
        {characters.includes(26) && <Character26 />}
        {characters.includes(27) && <Character27 />}
        {characters.includes(28) && <Character28 />}
        {characters.includes(29) && <Character29 />}
        {characters.includes(30) && <Character30 />}
        {characters.includes(31) && <Character31 />}
        {characters.includes(32) && <Character32 />}
        {characters.includes(33) && <Character33 />}
        {characters.includes(34) && <Character34 />}
        <TimeWeekDay>
          {t.平日} {playTimeWeekDayBegin}〜{playTimeWeekDayEnd}
          {t.時終了}
        </TimeWeekDay>
        <TimeWeekEnd>
          {t.土日} {playTimeWeekEndBegin}〜{playTimeWeekEndEnd}
          {t.時終了}
        </TimeWeekEnd>
        <FavoriteWeapon>
          <span id="favoriteWeapon">{valorantFavoriteWeapon}</span>
        </FavoriteWeapon>
        <Hitokoto>
          <span id={'hitokoto'}>{hitokoto}</span>
        </Hitokoto>
        <Rank>
          <span id="rank">{rank}</span>
        </Rank>
      </ProfileInner>
    </ProfileContainer>
  )
}

const ProfileContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #4c4c4c;
`

const ProfileInner = styled.div<{ fontColor: string }>`
  position: relative;
  // width: 320px;
  line-height: 0;

  * {
    line-height: 1;
    font-family: 'ap';
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const Name = styled.div`
  position: absolute;
  top: 10%;
  left: 27%;
  width: 52%;
  height: 20%;
  text-align: center;
  justify-content: center;
  word-break: break-all;
  display: flex;
  span {
    letter-spacing: -1px;
    font-weight: bold;
    line-height: 11px;
  }
`

const SexBase = styled.div`
  position: absolute;
  top: 0%;
  left: 73%;
  border-radius: 50%;
  border-style: solid;
  border-width: 2px;
  width: 28px;
  height: 28px;
`

const Sex0 = styled(SexBase)``
const Sex1 = styled(SexBase)`
  left: 81%;
`

const Sex2 = styled(SexBase)`
  left: 88.5%;
`

const PlatformBase = styled.div`
  position: absolute;
  top: 32%;

  border-style: solid;
  border-width: 2px;
  border-radius: 50%;
  width: 30px;
  height: 30px;
`

const Platform0 = styled(PlatformBase)`
  left: 38%;
`
const Platform1 = styled(PlatformBase)`
  left: 48%;
`
const Platform2 = styled(PlatformBase)`
  left: 58%;
`

const Platform3 = styled(PlatformBase)`
  left: 85%;
  width: 42px;
`

const VoiceChatBase = styled.div`
  position: absolute;
  top: 26.5%;
  border-style: solid;
  border-radius: 50%;
  border-width: 2px;
  width: 27px;
  height: 27px;
`

const VoiceChatText = styled.div`
  position: absolute;
  top: 22%;
  left: 42%;
  width: 14%;
  text-align: center;
`

const VoiceChatOk = styled(VoiceChatBase)`
  top: 18%;
  left: 46%;
  width: 32px;
  height: 18px;
  border-radius: 12px;
`

const VoiceChatNg = styled(VoiceChatBase)`
  top: 18%;
  left: 58%;
  width: 32px;
  height: 18px;
  border-radius: 12px;
`

const PlayStyle = styled.div`
  position: absolute;
  top: 40%;
  left: 55%;
  width: 40%;
  height: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-weight: bold;
    line-height: 1;
  }
`

const Rank = styled.span`
  position: absolute;
  top: 11%;
  left: 70%;
  width: 30%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 0.9;
  }
`

const TimeBase = styled.div`
  position: absolute;
  left: 0%;
  text-align: center;
  width: 36%;
  font-size: 13px;
  font-weight: bold;
  word-break: break-all;
  line-height: 1.2;
`

const TimeWeekDay = styled(TimeBase)`
  top: 32%;
`
const TimeWeekEnd = styled(TimeBase)`
  top: 36%;
`

const FavoriteWeapon = styled.div`
  position: absolute;
  top: 81%;
  left: 10%;
  width: 30%;
  height: 12%;
  text-align: center;
  word-break: break-all;

  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const CharacterBase = styled.div`
  position: absolute;
  border-style: solid;
  border-width: 2px;
  border-radius: 50%;
  width: 38px;
  height: 38px;
`

//
// ヴァンガード
//
export const Character0 = styled(CharacterBase)`
  top: 49.5%;
  left: 3.5%;
`
export const Character1 = styled(CharacterBase)`
  top: 49.5%;
  left: 14%;
`
export const Character2 = styled(CharacterBase)`
  top: 61%;
  left: 3.5%;
`
export const Character3 = styled(CharacterBase)`
  top: 61%;
  left: 13.5%;
`
export const Character4 = styled(CharacterBase)`
  top: 73%;
  left: 3%;
`
export const Character5 = styled(CharacterBase)`
  top: 73%;
  left: 13.5%;
`
export const Character6 = styled(CharacterBase)`
  top: 85%;
  left: 3.5%;
`

export const Character7 = styled(CharacterBase)`
  top: 85%;
  left: 13%;
`

//
// デュエリスト
//

export const Character8 = styled(CharacterBase)`
  top: 49.5%;
  left: 26%;
`

export const Character9 = styled(CharacterBase)`
  top: 49.5%;
  left: 35%;
`
export const Character10 = styled(CharacterBase)`
  top: 49.5%;
  left: 45%;
`
export const Character11 = styled(CharacterBase)`
  top: 49.5%;
  left: 56%;
`
export const Character12 = styled(CharacterBase)`
  top: 49.5%;
  left: 65%;
`
export const Character13 = styled(CharacterBase)`
  top: 62%;
  left: 26%;
`

export const Character14 = styled(CharacterBase)`
  top: 62%;
  left: 35.5%;
`
export const Character15 = styled(CharacterBase)`
  top: 62%;
  left: 46%;
`
export const Character16 = styled(CharacterBase)`
  top: 62%;
  left: 56%;
`
export const Character17 = styled(CharacterBase)`
  top: 62%;
  left: 66%;
`
export const Character18 = styled(CharacterBase)`
  top: 73%;
  left: 26%;
`
export const Character19 = styled(CharacterBase)`
  top: 73%;
  left: 36%;
`

export const Character20 = styled(CharacterBase)`
  top: 73%;
  left: 45.5%;
`
export const Character21 = styled(CharacterBase)`
  top: 73%;
  left: 56%;
`
export const Character22 = styled(CharacterBase)`
  top: 73%;
  left: 65%;
`
export const Character23 = styled(CharacterBase)`
  top: 85%;
  left: 31%;
`
export const Character24 = styled(CharacterBase)`
  top: 85%;
  left: 41%;
`
export const Character25 = styled(CharacterBase)`
  top: 85%;
  left: 50%;
`

export const Character26 = styled(CharacterBase)`
  top: 85%;
  left: 60.5%;
`

//
// ストラテジスト
//
export const Character27 = styled(CharacterBase)`
  top: 49.5%;
  left: 78%;
`

export const Character28 = styled(CharacterBase)`
  top: 49.5%;
  left: 88%;
`

export const Character29 = styled(CharacterBase)`
  top: 61%;
  left: 78%;
`

export const Character30 = styled(CharacterBase)`
  top: 61%;
  left: 88%;
`

export const Character31 = styled(CharacterBase)`
  top: 74%;
  left: 78%;
`

export const Character32 = styled(CharacterBase)`
  top: 74%;
  left: 88%;
`

export const Character33 = styled(CharacterBase)`
  top: 85%;
  left: 78%;
`

export const Character34 = styled(CharacterBase)`
  top: 85%;
  left: 88%;
`

const Hitokoto = styled.div`
  position: absolute;
  top: 20%;
  left: 70%;
  width: 28%;
  height: 20%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.1;
  }
`
